import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchEmpresas } from "../../slices/filters/reducer";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import AIDrawer from "./AIDrawer";
import { CMIContext } from "./cmi-context";

const SCREEN_TITLE = {
  finanzas:  "Dashboard Financiero",
  marketing: "Dashboard Marketing",
  proyectos: "Dashboard Operativa",
  ia:        "Automatización IA",
  config:    "Configuración",
};

const EMPRESA_LABEL_FALLBACK = { all: "Todas", 12: "Permiso", 13: "Visados", 14: "CVS" };

const isMobile = () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

const CMILayout = ({ children }) => {
  const dispatch = useDispatch();
  const { empresaId, year, empresasLoaded, empresasList } = useSelector((s) => s.Filters);
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiCtx, setAiCtx] = useState(null);

  useEffect(() => { if (!empresasLoaded) dispatch(fetchEmpresas()); }, [dispatch, empresasLoaded]);

  // Cierra el drawer móvil al navegar
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // En móvil bloquea el scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (mobileNavOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileNavOpen]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 900);
    window.dispatchEvent(new CustomEvent("cmi:refresh"));
  }, []);

  const openAI = useCallback((screen) => {
    setAiCtx({
      screen,
      title: SCREEN_TITLE[screen] || "Dashboard",
      company: (empresasList.find(e => e.id === empresaId) || {}).label || EMPRESA_LABEL_FALLBACK[empresaId] || String(empresaId),
      year,
    });
    setAiOpen(true);
  }, [empresaId, year]);

  // Hamburger: en móvil abre/cierra el drawer; en desktop colapsa a iconos
  const onToggleSidebar = useCallback(() => {
    if (isMobile()) setMobileNavOpen((v) => !v);
    else setCollapsed((c) => !c);
  }, []);

  const ctx = useMemo(() => ({ openAI, refreshing, onRefresh }), [openAI, refreshing, onRefresh]);

  return (
    <CMIContext.Provider value={ctx}>
      <div className={`cmi-app ${mobileNavOpen ? "nav-open" : ""}`}>
        <div className={`app ${collapsed ? "collapsed" : ""}`}>
          <Sidebar />
          <div className="main">
            <Header onToggleSidebar={onToggleSidebar} />
            <div className="page">
              {children}
            </div>
          </div>
        </div>
        <div className="nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden />
        <AIDrawer open={aiOpen} ctx={aiCtx} onClose={() => setAiOpen(false)} />
      </div>
    </CMIContext.Provider>
  );
};

export default CMILayout;
